import { hashSeed, mulberry32, range, streamRng } from '../math/rng.js';
import { BRAIN, PED, PED_TUNING, STATE } from '../actors/ai.js';

/**
 * @file THE POPULATION DIRECTOR: who is standing on the pavement, who is
 * sitting in the traffic, and whose corner this is.
 *
 * It is the city's replacement for `actors/Spawner.js`. The spawner's job was
 * "keep N hostiles in a ring around the player"; this one's is "make the city
 * look inhabited and give the fighting a reason", which is three different
 * populations with three different rules:
 *
 *  • **Civilians** stroll a graph of pavement waypoints derived once from
 *    `level.map.districts`, pause, and run when something frightens them. They
 *    are `BRAIN.CIVILIAN` slots in the same `EnemyManager` as everyone else.
 *  • **Gang members** are ordinary fighters, spawned on the pavements of the
 *    TERRITORY they own. Territories are split PER TOWN from a spec the game
 *    injects ({@link CrewRow}): a seeded angular share each where a town is
 *    shared — so standing on a border puts two hostile crews in each other's
 *    aggro band and a firefight starts without a single line of script — a
 *    whole town where one crew runs it, or a small pocket on a named compass
 *    edge, with the rest of that town belonging to nobody.
 *  • **Villagers** are the countryside's whole population. `POP.countryPeds` is
 *    0: the open road is empty and the people out of town are in the hamlets
 *    `map.villages` publishes, along with whichever crew that spec gives them.
 *  • **Occupants** ride the traffic. Every AI car that activates gets a driver
 *    and, a quarter of the time, a front passenger — plus, on 2-row bodies,
 *    the occasional rear passenger (`POP.rearChance` per rear seat). All are
 *    `BRAIN.SEATED` slots whose transform is slaved to `Traffic#getSeatWorld`
 *    every step.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS, NO LEVEL, NO ENGINE
 * ─────────────────────────────────────────────────────────────────────────────
 * Same deal as `traffic/Traffic.js`: this file takes a manager, a
 * `LevelMapData`, an optional `Traffic` and a couple of plain callbacks, so
 * `tests/factions.test.mjs` drives the whole thing — the waypoint graph, the
 * territory partition, the flee response, the seat slaving — in node with no
 * GPU. Everything visual is somebody else's problem.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WAYPOINT GRAPH IS A RING PER BLOCK, PLUS CROSSINGS
 * ─────────────────────────────────────────────────────────────────────────────
 * A block's pavement is the ~3.5 m ring inside its parcel rect, so a rect inset
 * by half of that (`PAVEMENT.inset`) walks straight down the middle of it.
 * Nodes are laid along that inset perimeter every `PAVEMENT.spacing` metres and
 * linked into a CYCLE (`next`/`prev`), which is why strolling is one array read
 * and not a path search. The four ring CORNERS additionally carry a `hop`: the
 * nearest corner on another block's ring, i.e. a crossing over the road at a
 * junction, where the zebras are. That is the entire graph — no A*, no
 * navmesh, no per-frame search.
 *
 * ⚠ It is built ONCE, from data, with no rng: two runs of a seed produce the
 * same pavements, and the graph does not change when the population does.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SEVERAL ISLANDS, SEVERAL CITIES
 * ─────────────────────────────────────────────────────────────────────────────
 * The world is an ARCHIPELAGO: `map.coastlines` is a list of closed rings, one
 * per island, and `map.cityRects` a list of paved plateaus, one per town. Three
 * things in this file had to stop assuming there was exactly one of each, and
 * all three failed the same way — by treating the gap between two islands as
 * ground:
 *
 *  • the BEACH loop walks each coastline ring separately. A flattened
 *    concatenation grows one segment running from the last point of island A to
 *    the first of island B, and this file would read that segment's normal as a
 *    shoreline and hand somebody a loitering point on the open sea.
 *  • "am I in town" is ANY city rect, not the first one — with one rect every
 *    pedestrian on the second island was a countryside stroller on a pavement.
 *  • TERRITORIES are split PER CITY. A wedge is a half-infinite sector; pointed
 *    across a strait it hands a crew turf it cannot walk to. See
 *    {@link buildTerritories}.
 *
 * A one-island level is a one-entry list of each and reaches byte-identical
 * results — that is the regression bar, and `map.coastline`/`map.cityRect`
 * (singular, pre-archipelago) are still accepted.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS DELIBERATELY NOT HERE
 * ─────────────────────────────────────────────────────────────────────────────
 * Police. An ambient patrol would make "the police are coming" meaningless, so
 * nothing here ever puts one on a pavement or in a car by itself. What the
 * director does publish is the SEAMS the response drives —
 * {@link createPopulation}'s `seatCrew` (an authored crew in a car the traffic
 * director has RESERVED), `adopt` (a retired officer joining the ambient books
 * and being culled by the ordinary despawn ring) and `alarm` — so
 * `systems/Wanted.js` owns every police decision and this file owns none of
 * them. See that file's header for the budgets.
 *
 * ⚠ And the FACTION TABLE ITSELF. Which crews exist, who hates whom and which
 * def id belongs to which side is GAME CONTENT (`src/game/factions.js`), so it
 * is INJECTED as {@link FactionRules} rather than imported. That is the same
 * seam `enemyArt.js` uses for the cast: the engine ships the machinery, the
 * game ships the roster, and the arrow never points from engine to game.
 *
 * @module engine/systems/Population
 */

/** @typedef {import('../actors/EnemyManager.js').EnemyManager} EnemyManager */
/** @typedef {import('../types.js').LevelMapData} LevelMapData */
/** @typedef {import('../types.js').Rng} Rng */

/**
 * The game's faction table, injected. `src/game/factions.js` is the shipped
 * implementation; anything with this shape works.
 *
 * @typedef {Object} FactionRules
 * @property {number} CIVILIAN The neutral faction id (0 by convention, so a
 *   zeroed `team` byte already means "harmless").
 * @property {number[]} GANGS The crew ids, in a stable order — the territory
 *   split hands out one wedge per entry.
 * @property {number} PLAYER Sentinel for "the player did this". Never a team.
 * @property {(a: number, b: number) => boolean} isHostile Symmetric.
 * @property {(id: string) => number} factionForId Def id -> faction.
 * @property {((place: {id?: string, kind: string}) => CrewRow[]|null)} [crewsFor]
 *   WHICH CREWS RUN THIS PLACE — a town (`kind: 'city'`, `id` the plateau's) or
 *   a village (`kind: 'village'`, `id` its name). OPTIONAL: a table without it
 *   (every dev scene, most fixtures) gets an even wedge per crew in every town
 *   and no crew at all out of town, which is what this file did before the
 *   island grew a gang geography. See {@link buildTerritories}.
 * @property {{isPlayerHostile: (f: number) => boolean, provoke: (f: number) => boolean,
 *   update: (dt: number) => void}} state The live provocation state.
 */

const TAU = Math.PI * 2;
const DEG = Math.PI / 180;

/**
 * The pavement graph's shape. `inset` is HALF the city's 3.5 m pavement ring,
 * so the nodes sit on its centre-line; `spacing` is a comfortable stride
 * between waypoints (closer and a civilian re-targets constantly, further and
 * it cuts corners across the kerb); `hopMax` has to clear the widest road plus
 * both insets measured CORNER TO CORNER — a 22 m boulevard junction is 25 m
 * across the diagonal.
 */
export const PAVEMENT = Object.freeze({
  inset: 1.75,
  spacing: 5,
  hopMax: 30,
  /** A block whose inset rect is smaller than this on either axis has no ring. */
  minSpan: 2,
});

/**
 * Everything the director's population rules are worth arguing about.
 *
 * ⚠ EVERY NUMBER HERE IS RELATIVE TO THE FOCUS, which is why not one of them
 * moved when the world went from a single 720 m island to two on a 2000 m
 * board. `ringMin`/`ringMax`/`despawn` are metres around the player at which
 * people appear and are forgotten;
 * `cityPeds`/`villagePeds`/`countryPeds`/`gangPerTerritory` are how many are
 * alive INSIDE that ring. A world twice as wide is not a
 * street with twice as many people on it — it is the same street, twice, and
 * the second one is 900 m away where nobody is standing. Scaling these with the
 * world size would buy nothing but a bigger actor pool and a slower cull pass.
 * A THIRD town and a handful of villages changed none of them either — a
 * village's five people are five people around YOU, in the one hamlet you are
 * at, whether the island holds two of them or ten.
 *
 * What the archipelago did change is WHICH rects count as "town" (there are
 * several) and about which centres the territories are split — geometry, not
 * budget.
 */
export const POP = Object.freeze({
  // ── the ring ──
  ringMin: 40,
  /**
   * The inner radius for a VILLAGE spawn, and it exists because a hamlet is
   * SMALLER THAN THE RING. The shipped `VILLAGE_RADIUS` is ~37 m, so from the
   * plaza every loitering point in the place is inside `ringMin` and the
   * village a player has just walked into is empty — and approaching one
   * head-on does not save it either, since a 37 m disc seen from 55 m out sits
   * almost entirely inside the 35-degree camera cone that nothing may spawn in.
   *
   * ⚠ The CONE is what stops somebody appearing in front of you; `ringMin` only
   * stops them appearing at your elbow. So this relaxes the radius and leaves
   * the cone alone: 25 m puts a villager on the far side of the plaza, behind
   * you or beside a house, which is where the alternative was nobody at all.
   */
  villageRingMin: 25,
  ringMax: 70,
  despawn: 95,
  /** Half-angle of the camera-forward cone nothing may appear inside. */
  coneDeg: 35,
  /** Spawn ATTEMPTS per second while under target. */
  rate: 8,
  /** Candidate positions per attempt. */
  tries: 6,
  /** Focus travel that invalidates the cached candidate node list. */
  candidateRefresh: 15,

  // ── how many ──
  cityPeds: 22,
  /**
   * ⚠ ZERO, AND THAT IS THE DESIGN. The open countryside is EMPTY: six people
   * ambling between beach and road-shoulder loitering points across a 2000 m
   * island read as strays who had walked out of town and kept going, and the
   * one reading a player never gave it was "these people live here". People
   * out of town live in the VILLAGES now ({@link POP.villagePeds}), which are
   * somewhere, and the road between them is empty, which is what a road
   * between two towns is.
   *
   * The number survives rather than the code around it: a level that wants a
   * populated hinterland tunes it back up and every path below still works.
   */
  countryPeds: 0,
  /**
   * Pedestrians around a VILLAGE — the countryside's whole population, and it
   * is a RING count like every other number here: it is what stands near you
   * in one hamlet, not what the island holds. Deliberately a quarter of
   * `cityPeds`: a dozen people on one street is a town, five is a village.
   */
  villagePeds: 5,
  /**
   * DEFAULT live members of a crew kept near the player, per territory in
   * range. A crew row in the game's territory table may override it with
   * `live` (see `game/factions.js#TERRITORY_CREWS`) — a town with a SOLE owner
   * wants more than a third of a three-way border did, and a two-block pocket
   * wants a corner presence rather than a garrison.
   */
  gangPerTerritory: 4,
  /** Hard cap on gang members alive at once, whatever the geometry says. */
  gangMax: 10,

  // ── strolling ──
  /** Chance a civilian at a ring CORNER crosses the road instead of turning. */
  hopChance: 0.18,
  /** Metres past which a civilian is too far from its stored node to resume. */
  reanchor: 15,
  /** Metres a countryside civilian will walk to its next loitering point. */
  countryGoal: 45,

  // ── cars ──
  /** Share of driving cars that carry a front passenger as well as a driver. */
  passengerChance: 0.25,
  /**
   * Chance PER REAR SEAT of a passenger in the back. Only a 2-row body has
   * rear seats at all (`Traffic#seatCount`), so the street average is well
   * under this.
   */
  rearChance: 0.15,
  /**
   * Share of DRIVERS who are a member of the crew whose turf the car set off
   * on, rather than a civilian. A crew that never leaves the pavement is a
   * crew you can only meet on foot — and the carjack sequence has to be able
   * to pull the wrong man out of the wrong car, which is where the whole
   * "a stolen car has consequences" reading comes from. Kept low: three cars
   * in twenty, so the street still reads as ordinary traffic.
   *
   * ⚠ A seated gang member is INERT (`BRAIN.SEATED` skips the fighter step)
   * and is skipped by `EnemyManager#_acquire`, so a rival never chases a car.
   * He becomes a fighter the moment he is dragged out of it.
   */
  gangDriverChance: 0.15,
});

/**
 * Metres of slack around a city's paved plateau that still read as "in town".
 *
 * The plateau is the CARRIAGEWAYS; the pavements, the kerbside furniture and the
 * first stretch of country road all sit just outside it, so a focus standing on
 * the ring road is downtown by any reading a player would give it. One margin,
 * one number, applied to EVERY city rect — a second town does not get a second
 * opinion about where its edge is.
 */
const CITY_MARGIN = 40;

// ─────────────────────────────────────────────────────────────────────────────
// The map's shape, normalised
// ─────────────────────────────────────────────────────────────────────────────
//
// THE ONLY TWO PLACES IN THIS FILE THAT KNOW THE MAP'S FIELD NAMES. Everything
// downstream sees a list — of rings, of rects — and cannot tell a one-island
// level from an archipelago. That is deliberate: the singular fields are the
// pre-archipelago contract and a level that still publishes them must keep
// working unchanged, but nothing may branch on which it got.

/**
 * The fallback town for a map that publishes no plateau at all — a dev scene, a
 * toy fixture. Unchanged from the day this file was written; it is what makes
 * `createPopulation` survive a map with nothing but districts in it.
 */
const DEFAULT_CITY_RECT = Object.freeze({ x: -150, z: -150, w: 300, d: 300 });

/**
 * Every island's waterline, as a LIST OF CLOSED RINGS.
 *
 * ⚠ NEVER FLATTEN THE RESULT. Each ring is closed and its first point is NOT
 * repeated (`types.js`), so every consumer walks it with `(i + 1) % ring.length`
 * — and a concatenation of two islands' rings therefore carries one extra
 * segment, running from the last point of island A to the first point of island
 * B, straight across the open sea. {@link buildCountryPoints} would take that
 * segment's normal for a shoreline and put a loitering point out on the water,
 * silently. That is why `Archipelago.js` RENAMED the property rather than
 * widening it: a consumer nobody updated throws on `undefined` instead of
 * walking pedestrians into the strait.
 *
 * `map.coastline` (singular) is the pre-archipelago field and is still read; a
 * one-island level is a one-entry list and the code below cannot tell.
 *
 * @param {LevelMapData} map
 * @returns {Array<Array<{x: number, z: number}>>} Possibly empty (a flat level).
 */
function coastRings(map) {
  const many = map?.coastlines;
  // A ring needs three points to have an inside; anything shorter is a stray.
  if (Array.isArray(many)) return many.filter((r) => Array.isArray(r) && r.length >= 3);
  const one = map?.coastline;
  return Array.isArray(one) && one.length >= 3 ? [one] : [];
}

/**
 * Every city's paved plateau, MIN-corner rects. `map.cityRects` is the
 * archipelago's list; `map.cityRect` is the FIRST of them and predates it; the
 * hard-coded box is the last resort.
 *
 * Two things read it and both mean "town": how many pedestrians the focus is
 * worth ({@link CITY_MARGIN}), and which centres the gang territories are split
 * about.
 *
 * @param {LevelMapData} map
 * @returns {Array<{x: number, z: number, w: number, d: number}>} NEVER empty —
 *   a caller that had to test for that would grow a second definition of "no
 *   city", and `inCity` returning false everywhere is already exactly that.
 */
function cityRectsOf(map) {
  const isRect = (r) => !!r && Number.isFinite(r.x) && Number.isFinite(r.z) && r.w > 0 && r.d > 0;
  const many = Array.isArray(map?.cityRects) ? map.cityRects.filter(isRect) : [];
  if (many.length) return many;
  if (isRect(map?.cityRect)) return [map.cityRect];
  return [DEFAULT_CITY_RECT];
}

/**
 * Every VILLAGE on the board — the countryside's settlements, as discs.
 *
 * ⚠ READ DEFENSIVELY AND NEVER REQUIRED. `map.villages` is published by the
 * level's rural pass and an absent, empty or malformed list means exactly one
 * thing: this level has no villages, the countryside is empty, and every path
 * below simply does nothing. That is the same contract `coastRings` has for a
 * flat level, and it is what lets this file ship before (or without) the
 * generator that fills the list.
 *
 * @param {LevelMapData & {villages?: Array<any>}} map
 * @returns {Array<{x: number, z: number, r: number, name: string}>} Possibly empty.
 */
function villagesOf(map) {
  const raw = /** @type {any} */ (map)?.villages;
  if (!Array.isArray(raw)) return [];
  /** @type {Array<{x: number, z: number, r: number, name: string}>} */
  const out = [];
  for (let i = 0; i < raw.length; i++) {
    const v = raw[i];
    if (!v || !Number.isFinite(v.x) || !Number.isFinite(v.z) || !(v.r > 0)) continue;
    out.push({ x: v.x, z: v.z, r: v.r, name: String(v.name ?? v.id ?? i) });
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// The pavement graph
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} PavementGraph
 * @property {number} n Node count.
 * @property {Float32Array} x @property {Float32Array} z World positions.
 * @property {Int32Array} next  Next node along this node's ring (a cycle).
 * @property {Int32Array} prev  Previous node along it.
 * @property {Int32Array} hop   A node on ANOTHER block's ring, or -1. Only ring
 *   corners ever carry one — that is where the crossings are.
 * @property {Int32Array} block District index this node's ring belongs to.
 * @property {Uint8Array} corner 1 at the four ring corners.
 * @property {Int32Array} ringStart First node of each district's ring.
 * @property {Int32Array} ringCount Nodes in it (0 for a block with no ring).
 * @property {number} rings Districts that produced a ring.
 * @property {number} hops Corners that found a crossing partner.
 */

/**
 * Derives the pavement waypoint graph from a level's districts. PURE and
 * rng-free: same districts, same graph, forever.
 *
 * @param {Array<{blockId?: string, rect: {x: number, z: number, w: number, d: number}}>} districts
 * @param {Object} [o] Overrides for {@link PAVEMENT}.
 * @returns {PavementGraph}
 */
export function buildPavementGraph(districts, o = {}) {
  const inset = o.inset ?? PAVEMENT.inset;
  const spacing = o.spacing ?? PAVEMENT.spacing;
  const hopMax = o.hopMax ?? PAVEMENT.hopMax;
  const minSpan = o.minSpan ?? PAVEMENT.minSpan;

  /** @type {number[]} */
  const xs = [];
  /** @type {number[]} */
  const zs = [];
  /** @type {number[]} */
  const blockOf = [];
  /** @type {number[]} */
  const cornerAt = [];
  const list = districts ?? [];
  const ringStart = new Int32Array(list.length).fill(-1);
  const ringCount = new Int32Array(list.length);
  let rings = 0;

  for (let b = 0; b < list.length; b++) {
    const r = list[b]?.rect;
    if (!r) continue;
    const x0 = r.x + inset;
    const z0 = r.z + inset;
    const x1 = r.x + r.w - inset;
    const z1 = r.z + r.d - inset;
    if (x1 - x0 < minSpan || z1 - z0 < minSpan) continue;

    const start = xs.length;
    // Four sides, walked in order. Each side emits its START corner and its
    // interior points; the end corner belongs to the next side, which is what
    // makes the whole thing one closed cycle with no duplicated node.
    const sides = [
      [x0, z0, x1, z0],
      [x1, z0, x1, z1],
      [x1, z1, x0, z1],
      [x0, z1, x0, z0],
    ];
    for (const [ax, az, bx, bz] of sides) {
      const len = Math.hypot(bx - ax, bz - az);
      const steps = Math.max(1, Math.round(len / spacing));
      for (let k = 0; k < steps; k++) {
        const t = k / steps;
        xs.push(ax + (bx - ax) * t);
        zs.push(az + (bz - az) * t);
        blockOf.push(b);
        cornerAt.push(k === 0 ? 1 : 0);
      }
    }
    ringStart[b] = start;
    ringCount[b] = xs.length - start;
    rings++;
  }

  const n = xs.length;
  const g = {
    n,
    x: Float32Array.from(xs),
    z: Float32Array.from(zs),
    next: new Int32Array(n).fill(-1),
    prev: new Int32Array(n).fill(-1),
    hop: new Int32Array(n).fill(-1),
    block: Int32Array.from(blockOf),
    corner: Uint8Array.from(cornerAt),
    ringStart,
    ringCount,
    rings,
    hops: 0,
  };

  for (let b = 0; b < list.length; b++) {
    const s = ringStart[b];
    const c = ringCount[b];
    if (s < 0 || c === 0) continue;
    for (let k = 0; k < c; k++) {
      g.next[s + k] = s + ((k + 1) % c);
      g.prev[s + k] = s + ((k + c - 1) % c);
    }
  }

  // Crossings. Corner-to-corner, nearest wins, ties break to the lower index —
  // so the links are a pure function of the geometry. O(corners²), and there
  // are four per block: 28 blocks is 112 corners, i.e. nothing.
  /** @type {number[]} */
  const corners = [];
  for (let i = 0; i < n; i++) if (g.corner[i]) corners.push(i);
  const max2 = hopMax * hopMax;
  for (const i of corners) {
    let best = -1;
    let bestD2 = max2;
    for (const j of corners) {
      if (g.block[j] === g.block[i]) continue;
      const dx = g.x[j] - g.x[i];
      const dz = g.z[j] - g.z[i];
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = j;
      }
    }
    g.hop[i] = best;
    if (best >= 0) g.hops++;
  }

  return g;
}

// ─────────────────────────────────────────────────────────────────────────────
// Territories
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ONE CREW'S CLAIM ON ONE PLACE. Content, injected — see {@link FactionRules}.
 *
 * @typedef {Object} CrewRow
 * @property {number} gang The crew's faction id.
 * @property {number} [share=1] Fraction of the town's BLOCKS (a pocket) or of
 *   the angle around its centre (a wedge), relative to the other rows.
 * @property {string} [at] A compass edge (`'n'|'ne'|'e'|'se'|'s'|'sw'|'w'|'nw'`,
 *   north being -Z). Present = this row is a POCKET anchored there; absent =
 *   this row is a WEDGE and the wedges divide whatever the pockets left.
 * @property {number} [live] Live members the director keeps near the player
 *   while they are in this place. 0/absent = `POP.gangPerTerritory`.
 */

/**
 * The eight compass directions as unit-ish vectors. NORTH IS -Z, matching the
 * map ("south is map-down", `ui/hud/mapRender.js`) and every `prefer: 'south'`
 * in the mission placement.
 */
const COMPASS = Object.freeze({
  n: { x: 0, z: -1 },
  ne: { x: Math.SQRT1_2, z: -Math.SQRT1_2 },
  e: { x: 1, z: 0 },
  se: { x: Math.SQRT1_2, z: Math.SQRT1_2 },
  s: { x: 0, z: 1 },
  sw: { x: -Math.SQRT1_2, z: Math.SQRT1_2 },
  w: { x: -1, z: 0 },
  nw: { x: -Math.SQRT1_2, z: -Math.SQRT1_2 },
});

/**
 * @typedef {Object} Territories
 * @property {Int32Array} faction Owning {@link FACTION} per district index —
 *   the NEUTRAL id (`o.neutral`, the civilian faction) for a block no crew
 *   claimed, which is most of a town whose spec is all pockets.
 * @property {Int32Array} city Which CITY each district was split with — an index
 *   into the `cities` list. A crew's turf never crosses this line, which is what
 *   makes "one contiguous patch" a per-city statement on an archipelago.
 * @property {number} base Seeded base angle of the FIRST city's split, radians.
 *   Kept as a scalar for the read-outs that predate the archipelago.
 * @property {number[]} bases One base angle per city, in city order.
 * @property {{x: number, z: number}} centre The first city's split origin.
 * @property {Array<{x: number, z: number}>} centres One origin per city.
 * @property {number[]} counts Blocks per crew across the WHOLE map, indexed like
 *   the `gangs` list.
 * @property {number} unclaimed Blocks belonging to NOBODY, across the map.
 * @property {Int32Array} live Per (city, crew) live budget, `city * gangs +
 *   crew`; 0 means "the director's default". See {@link CrewRow}.
 * @property {(city: number, crew: number) => number} liveOf Reads {@link live}
 *   with the bounds checks, because the caller has a faction and a block.
 * @property {number} repaired Blocks moved by the contiguity repair, summed over
 *   the cities.
 */

/**
 * Splits every city's blocks between the crews THAT CITY'S SPEC NAMES, about
 * THAT CITY'S OWN centre — a seeded angular share each, or a small pocket
 * anchored on a compass edge, or nothing at all.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE SPLIT PER CITY, AND THE ARCHIPELAGO FORCES IT
 * ─────────────────────────────────────────────────────────────────────────────
 * This was one three-way wedge about one centre, which is exactly right for one
 * city and nonsense for two. A wedge is a HALF-INFINITE SECTOR: aim it across a
 * strait and the crew that owns downtown A's north side also owns whatever of
 * town B happens to fall on the same bearing — a turf you cannot walk, half of
 * it on another island, and no amount of "the crews have a presence in both
 * towns" makes a player read it that way when the border runs through open sea.
 *
 * The contiguity repair below cannot rescue that either, and it is worth being
 * precise about why: two blocks on different islands are never adjacent (there
 * is 150 m of water between the coasts and `blockAdjacency`'s gap is 26 m), so a
 * crew straddling the strait is TWO components by construction. The repair keeps
 * the largest and gives the other away — every time, on every seed. The outcome
 * would then be decided by which island has more blocks rather than by the seed,
 * and a crew could be pushed out of a whole town by an off-by-one in the grid.
 *
 * So each city is split on its OWN axis about its OWN centre and repaired
 * against its OWN blocks. The same three crews run both towns, which is the
 * reading a player gives it anyway, and it means no crew is ever homeless
 * because one island's grid came out lopsided.
 *
 * ⚠ ONE `rng()` PER CITY, DRAWN IN CITY ORDER, WHATEVER THE GEOMETRY TURNS OUT
 * TO BE — including for a city that ended up holding no blocks at all. Same
 * house rule as the occupant draws further down: a branch that skips a draw
 * makes town B's axis depend on how town A's grid came out, and "same seed, same
 * city" stops being true for the town that is still standing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WEDGES, POCKETS, AND GROUND THAT BELONGS TO NOBODY
 * ─────────────────────────────────────────────────────────────────────────────
 * A city's spec is a list of {@link CrewRow}s, and there are exactly two kinds:
 *
 *  • A WEDGE (no `at`) takes a seeded angular slice sized by its `share`. Three
 *    equal wedges is the DEFAULT and is what a city with no spec still gets, so
 *    every dev scene and every fixture behaves exactly as this function always
 *    did. ONE wedge row hands that crew the whole town, which is how a town
 *    gets a sole owner without a special case.
 *  • A POCKET (`at: 'n'`, `'sw'`, ...) takes `share` of the town's BLOCKS,
 *    anchored on the block furthest out along that compass bearing and grown
 *    across ADJACENT blocks — so it lands on that edge of that town on every
 *    seed, and it is one connected patch BY CONSTRUCTION rather than by luck.
 *
 * Pockets are carved first; the wedges divide what is left; and whatever no row
 * claims belongs to NOBODY (`o.neutral`) — which is the point of the mechanism,
 * because a town of three small pockets is a town that is mostly CIVILIAN, and
 * `tryGang` finds no crew pool for neutral ground and quietly spawns nobody.
 *
 * ⚠ A POCKET MUST BE ITS CREW'S LARGEST COMPONENT IN THAT TOWN or the repair
 * below donates it away. One row per crew per town keeps that true; two pockets
 * for one crew in one town is a spec bug the repair will silently "fix".
 *
 * A wedge is contiguous by construction over a convex-ish block field, but "by
 * construction" is doing a lot of work at a corner where three 120-degree
 * boundaries meet a lumpy grid, and one orphaned block belonging to a crew it
 * cannot walk to is exactly the kind of thing a player notices and nobody can
 * explain. So the split is followed by a REPAIR: any component of a crew's
 * territory that is not its largest gets handed to whichever neighbouring crew
 * it shares the most edges with. A crew always keeps its largest component, so
 * the repair can never empty one — and it never touches unclaimed ground, in
 * either direction: neutral is a decision, not an orphan.
 *
 * @param {Array<{rect: {x: number, z: number, w: number, d: number}}>} districts
 * @param {Object} o
 * @param {Rng} o.rng The `'population.territory'` stream — see rule 4.
 * @param {Array<{x: number, z: number, w?: number, d?: number, crews?: CrewRow[]|null}>} [o.cities]
 *   Every city's paved plateau (`map.cityRects`), optionally carrying its crew
 *   spec. A block belongs to the rect it stands on, or failing that to the
 *   nearest one. Bare `{x, z}` centres are accepted for a caller that has no
 *   rects.
 * @param {{x: number, z: number}} [o.centre] The single-city form this function
 *   shipped with. Exactly equivalent to a one-entry `cities`.
 * @param {number[]} o.gangs The crew ids. A city with no spec gets one even
 *   wedge per entry.
 * @param {number} [o.neutral=0] The faction id written for a block no crew
 *   claimed. `createPopulation` passes `factions.CIVILIAN`; 0 is that by
 *   convention (`game/factions.js`).
 * @param {number} [o.adjacencyGap=26] Metres of road between two rects that
 *   still counts as "next door". A boulevard is 22 m wide.
 * @returns {Territories}
 */
export function buildTerritories(districts, o) {
  const list = districts ?? [];
  const { rng, gangs, adjacencyGap = 26, neutral = 0 } = o;
  const cities = territoryCities(o);
  const n = list.length;
  const G = gangs.length;

  /** Sector (index into `gangs`) per block, -1 = nobody. */
  const sector = new Int32Array(n).fill(-1);
  const cityOf = new Int32Array(n);
  for (let i = 0; i < n; i++) cityOf[i] = nearestCity(list[i].rect, cities);

  // Adjacency is derived ONCE over the whole map and then RESTRICTED per city
  // below. Blocks on different islands are a strait apart and could never be
  // neighbours anyway — restricting it explicitly is what makes "a repair never
  // reaches across the water" a property of this code rather than a property of
  // how far apart somebody happened to put the islands.
  const adj = blockAdjacency(list, adjacencyGap);

  /** @type {number[]} */
  const bases = [];
  const live = new Int32Array(cities.length * G);
  let repaired = 0;

  for (let c = 0; c < cities.length; c++) {
    // ⚠ Unconditional, before any geometry is looked at — see the header. It is
    // drawn for a city with no blocks, and for one whose spec is all pockets and
    // never looks at it: a branch that skipped it would make the NEXT town's
    // axis depend on how this one was written.
    const base = rng() * TAU;
    bases.push(base);

    const rows = crewRows(cities[c].crews, gangs);
    for (const row of rows) live[c * G + row.sector] = row.live;

    /** @type {number[]} This city's blocks, as GLOBAL district indices. */
    const mine = [];
    for (let i = 0; i < n; i++) if (cityOf[i] === c) mine.push(i);
    const m = mine.length;
    if (!m || !rows.length) continue;

    const centre = cities[c].centre;
    /** Sector per LOCAL index. The repair wants a dense 0..m-1 world. */
    const local = new Int32Array(m).fill(-1);
    /** Block centres, once — every pass below wants them. */
    const cx = new Float64Array(m);
    const cz = new Float64Array(m);
    for (let k = 0; k < m; k++) {
      const r = list[mine[k]].rect;
      cx[k] = r.x + r.w / 2;
      cz[k] = r.z + r.d / 2;
    }

    // The repair runs on THIS city's blocks only: `localAdj` drops every edge
    // that leaves the town, so an orphan can only ever be handed to a crew it
    // shares a street with. (For a one-city map this is the global graph,
    // re-indexed by the identity.) The POCKET growth uses the same restriction,
    // which is what stops a pocket reaching into the next town.
    /** @type {Map<number, number>} Global district index -> local index. */
    const localOf = new Map();
    for (let k = 0; k < m; k++) localOf.set(mine[k], k);
    const localAdj = mine.map((i) => {
      /** @type {number[]} */
      const out = [];
      for (const j of adj[i]) {
        const k = localOf.get(j);
        if (k !== undefined) out.push(k);
      }
      return out;
    });

    // ── 1. POCKETS, in row order, on the edges they name ────────────────────
    for (const row of rows) {
      if (!row.dir) continue;
      carvePocket(local, localAdj, cx, cz, centre, row, Math.max(1, Math.round(row.share * m)));
    }

    // ── 2. WEDGES over whatever is left ─────────────────────────────────────
    const wedges = rows.filter((r) => !r.dir);
    if (wedges.length) {
      // Cumulative shares as angles. With the default (one row per crew, share
      // 1) these are the plain even wedges this function shipped with.
      let acc = 0;
      const total = wedges.reduce((a, r) => a + r.share, 0);
      const bound = wedges.map((r) => ((acc += r.share) / total) * TAU);
      for (let k = 0; k < m; k++) {
        if (local[k] >= 0) continue;
        const a = Math.atan2(cz[k] - centre.z, cx[k] - centre.x) - base;
        const u = ((a % TAU) + TAU) % TAU;
        let w = 0;
        while (w < wedges.length - 1 && u >= bound[w]) w++;
        local[k] = wedges[w].sector;
      }

      // Nothing at all in a wedge (a very lopsided island): give it the block
      // whose bearing is closest to the wedge's centre-line. A crew the spec
      // NAMES has territory — that is a design invariant, not an accident of
      // the seed — and it is enforced PER TOWN, so a crew holds a corner of
      // each rather than all of one.
      for (let w = 0; w < wedges.length; w++) {
        const s = wedges[w].sector;
        let count = 0;
        for (let k = 0; k < m; k++) if (local[k] === s) count++;
        if (count > 0) continue;
        const want = base + ((w > 0 ? bound[w - 1] : 0) + bound[w]) / 2;
        let best = -1;
        let bestD = Infinity;
        for (let k = 0; k < m; k++) {
          // Only another WEDGE's ground is up for grabs: a pocket is placed on
          // purpose and unclaimed ground belongs to nobody on purpose.
          const owner = local[k];
          if (owner < 0 || !wedges.some((r) => r.sector === owner)) continue;
          const a = Math.atan2(cz[k] - centre.z, cx[k] - centre.x);
          let d = Math.abs(((a - want + Math.PI) % TAU + TAU) % TAU - Math.PI);
          // Never strip a crew that would then be empty itself.
          let owns = 0;
          for (let q = 0; q < m; q++) if (local[q] === owner) owns++;
          if (owns <= 1) d = Infinity;
          if (d < bestD) {
            bestD = d;
            best = k;
          }
        }
        if (best >= 0) local[best] = s;
      }
    }

    // ── 3. CONTIGUITY ───────────────────────────────────────────────────────
    repaired += repairContiguity(local, localAdj, G);

    for (let k = 0; k < m; k++) sector[mine[k]] = local[k];
  }

  const faction = new Int32Array(n);
  const counts = gangs.map(() => 0);
  let unclaimed = 0;
  for (let i = 0; i < n; i++) {
    const s = sector[i];
    if (s < 0) {
      faction[i] = neutral;
      unclaimed++;
    } else {
      faction[i] = gangs[s];
      counts[s]++;
    }
  }
  return {
    faction,
    city: cityOf,
    base: bases[0] ?? 0,
    bases,
    centre: { ...cities[0].centre },
    centres: cities.map((c) => ({ ...c.centre })),
    counts,
    unclaimed,
    live,
    liveOf: (city, crew) => (city >= 0 && city < cities.length && crew >= 0 && crew < G
      ? live[city * G + crew]
      : 0),
    repaired,
  };
}

/**
 * Normalises a city's {@link CrewRow} list against the crews this level
 * actually carries.
 *
 * `null`/undefined (and anything that is not a list) means NO SPEC: one even
 * wedge per crew, which is what this file did before towns had specs. An empty
 * list is a spec — a town no crew runs — and is honoured as one.
 *
 * @param {CrewRow[]|null|undefined} crews
 * @param {number[]} gangs
 * @returns {Array<{sector: number, share: number, dir: {x: number, z: number}|null, live: number}>}
 */
function crewRows(crews, gangs) {
  if (!Array.isArray(crews)) return gangs.map((_, s) => ({ sector: s, share: 1, dir: null, live: 0 }));
  const out = [];
  for (const row of crews) {
    const sector = gangs.indexOf(row?.gang);
    // A row naming a crew this level does not carry is DROPPED, not an error:
    // the same content table has to work for a level with a shorter roster.
    if (sector < 0) continue;
    out.push({
      sector,
      share: Number.isFinite(row?.share) && row.share > 0 ? row.share : 1,
      dir: COMPASS[String(row?.at ?? '').toLowerCase()] ?? null,
      live: Number.isFinite(row?.live) && row.live > 0 ? Math.floor(row.live) : 0,
    });
  }
  return out;
}

/**
 * Carves ONE pocket into a city's local sector array, in place: the unclaimed
 * block furthest along `row.dir` from the town centre, plus the best-scoring
 * unclaimed neighbours of what it has so far, until it holds `want` blocks or
 * runs out of neighbours.
 *
 * Growing along ADJACENCY rather than by score alone is the whole design: it is
 * what makes the patch one connected component before the repair ever looks at
 * it, and a pocket that cannot grow (a lone block across a boulevard) stops
 * SMALL rather than teleporting across the town.
 *
 * No rng at all — ties break to the lowest local index, so the same town yields
 * the same pocket on every seed. The SEED moves the wedges; the compass does
 * not move.
 *
 * @param {Int32Array} local Mutated.
 * @param {number[][]} adj Local adjacency.
 * @param {Float64Array} cx @param {Float64Array} cz Block centres.
 * @param {{x: number, z: number}} centre The town's centre.
 * @param {{sector: number, dir: {x: number, z: number}|null}} row
 * @param {number} want Blocks to claim, at least 1.
 * @returns {number} Blocks actually claimed.
 */
function carvePocket(local, adj, cx, cz, centre, row, want) {
  const dir = row.dir;
  if (!dir) return 0;
  const m = local.length;
  const score = (k) => (cx[k] - centre.x) * dir.x + (cz[k] - centre.z) * dir.z;

  let anchor = -1;
  for (let k = 0; k < m; k++) {
    if (local[k] >= 0) continue;
    if (anchor < 0 || score(k) > score(anchor)) anchor = k;
  }
  if (anchor < 0) return 0;
  local[anchor] = row.sector;

  let taken = 1;
  while (taken < want) {
    let best = -1;
    for (let k = 0; k < m; k++) {
      if (local[k] >= 0) continue;
      let touches = false;
      for (const j of adj[k]) {
        if (local[j] === row.sector) {
          touches = true;
          break;
        }
      }
      if (!touches) continue;
      if (best < 0 || score(k) > score(best)) best = k;
    }
    if (best < 0) break;
    local[best] = row.sector;
    taken++;
  }
  return taken;
}

/**
 * Normalises {@link buildTerritories}'s `cities` / `centre` options into one
 * list of `{rect, centre}`. Anything carrying a positive `w`/`d` is a plateau
 * rect and gets its centre derived; a bare `{x, z}` is a centre with no rect
 * (the single-city form). Never returns an empty list — a split about the world
 * origin is a sane answer for a map that publishes no town, and the alternative
 * is a second definition of "no city" three call sites deep.
 *
 * @param {Object} o
 * @returns {Array<{rect: {x: number, z: number, w: number, d: number}|null, centre: {x: number, z: number}}>}
 */
function territoryCities(o) {
  const raw = Array.isArray(o?.cities) && o.cities.length ? o.cities : (o?.centre ? [o.centre] : []);
  const out = [];
  for (const c of raw) {
    if (!c || !Number.isFinite(c.x) || !Number.isFinite(c.z)) continue;
    const rect = c.w > 0 && c.d > 0 ? c : null;
    out.push({
      rect,
      centre: rect ? { x: rect.x + rect.w / 2, z: rect.z + rect.d / 2 } : { x: c.x, z: c.z },
      // ⚠ `undefined` and `null` both mean NO SPEC (even wedges); an empty list
      // means a town no crew runs. `crewRows` is where that distinction lives.
      crews: Array.isArray(c.crews) ? c.crews : null,
    });
  }
  return out.length ? out : [{ rect: null, centre: { x: 0, z: 0 }, crews: null }];
}

/**
 * Which town a block belongs to: the plateau it stands on, or — for a block the
 * lattice pushed past the pavement — the nearest one.
 *
 * Distance is to the RECT and not to its centre, so a long thin town cannot
 * steal a block from a compact one simply by having a further-away middle. Ties
 * break to the lower index, and there is no rng anywhere: the partition is a
 * pure function of the geometry, which is what lets the wedge draws stay in
 * city order.
 *
 * @param {{x: number, z: number, w: number, d: number}} r The block's rect.
 * @param {Array<{rect: any, centre: {x: number, z: number}}>} cities
 * @returns {number} Index into `cities`.
 */
function nearestCity(r, cities) {
  if (cities.length === 1) return 0;
  const cx = r.x + r.w / 2;
  const cz = r.z + r.d / 2;
  let best = 0;
  let bestD2 = Infinity;
  for (let c = 0; c < cities.length; c++) {
    const rect = cities[c].rect;
    let d2;
    if (rect) {
      // Zero inside the rect, so a block standing on a plateau always wins it.
      const dx = Math.max(rect.x - cx, 0, cx - (rect.x + rect.w));
      const dz = Math.max(rect.z - cz, 0, cz - (rect.z + rect.d));
      d2 = dx * dx + dz * dz;
    } else {
      d2 = (cities[c].centre.x - cx) ** 2 + (cities[c].centre.z - cz) ** 2;
    }
    if (d2 < bestD2) {
      bestD2 = d2;
      best = c;
    }
  }
  return best;
}

/**
 * Which blocks touch which, across a road. Two rects are neighbours when they
 * overlap on one axis and are within `gap` on the other.
 * @param {Array<{rect: {x: number, z: number, w: number, d: number}}>} list
 * @param {number} gap
 * @returns {number[][]}
 */
function blockAdjacency(list, gap) {
  const n = list.length;
  /** @type {number[][]} */
  const adj = Array.from({ length: n }, () => []);
  for (let i = 0; i < n; i++) {
    const a = list[i].rect;
    for (let j = i + 1; j < n; j++) {
      const b = list[j].rect;
      const overlapX = a.x < b.x + b.w + 1e-3 && b.x < a.x + a.w + 1e-3;
      const overlapZ = a.z < b.z + b.d + 1e-3 && b.z < a.z + a.d + 1e-3;
      const gapX = Math.max(0, Math.max(a.x - (b.x + b.w), b.x - (a.x + a.w)));
      const gapZ = Math.max(0, Math.max(a.z - (b.z + b.d), b.z - (a.z + a.d)));
      const touching = (overlapZ && gapX <= gap && gapZ === 0) || (overlapX && gapZ <= gap && gapX === 0);
      if (!touching) continue;
      adj[i].push(j);
      adj[j].push(i);
    }
  }
  return adj;
}

/**
 * Makes every sector one connected component, in place. See
 * {@link buildTerritories} for why. Deterministic (lowest index wins every
 * tie) and bounded (12 passes; a partition this small settles in one or two).
 *
 * ⚠ A NEGATIVE SECTOR IS UNCLAIMED GROUND AND THIS PASS IGNORES IT ENTIRELY —
 * it is never labelled, never donated and never voted for. "Nobody runs these
 * blocks" is a decision the spec made, and a town of pockets would otherwise
 * have its civilian majority handed to whichever crew happened to border most
 * of it, which is the exact opposite of what the spec asked for.
 *
 * @param {Int32Array} sector Mutated. -1 = unclaimed.
 * @param {number[][]} adj
 * @param {number} sectors
 * @returns {number} Blocks moved.
 */
function repairContiguity(sector, adj, sectors) {
  const n = sector.length;
  let moved = 0;
  for (let pass = 0; pass < 12; pass++) {
    // Label components within each sector.
    const comp = new Int32Array(n).fill(-1);
    /** @type {number[][]} */
    const members = [];
    const stack = [];
    for (let i = 0; i < n; i++) {
      if (comp[i] >= 0 || sector[i] < 0) continue;
      const id = members.length;
      const bag = [];
      comp[i] = id;
      stack.length = 0;
      stack.push(i);
      while (stack.length) {
        const k = /** @type {number} */ (stack.pop());
        bag.push(k);
        for (const j of adj[k]) {
          if (comp[j] < 0 && sector[j] === sector[k]) {
            comp[j] = id;
            stack.push(j);
          }
        }
      }
      members.push(bag);
    }

    // The biggest component of each sector is the one that stays.
    const keep = new Int32Array(sectors).fill(-1);
    const keepSize = new Int32Array(sectors);
    for (let c = 0; c < members.length; c++) {
      const s = sector[members[c][0]];
      if (members[c].length > keepSize[s]) {
        keepSize[s] = members[c].length;
        keep[s] = c;
      }
    }

    let changed = 0;
    for (let c = 0; c < members.length; c++) {
      const s = sector[members[c][0]];
      if (keep[s] === c) continue;
      // Hand the orphan to the neighbouring sector it shares the most edges
      // with; if it has no neighbour at all (an island block) leave it be.
      const votes = new Int32Array(sectors);
      for (const i of members[c]) {
        for (const j of adj[i]) if (comp[j] !== c && sector[j] >= 0) votes[sector[j]]++;
      }
      let best = -1;
      for (let s2 = 0; s2 < sectors; s2++) {
        if (s2 === s) continue;
        if (votes[s2] > 0 && (best < 0 || votes[s2] > votes[best])) best = s2;
      }
      if (best < 0) continue;
      for (const i of members[c]) sector[i] = best;
      changed += members[c].length;
    }
    moved += changed;
    if (changed === 0) break;
  }
  return moved;
}

// ─────────────────────────────────────────────────────────────────────────────
// The director
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {Object} o
 * @param {EnemyManager} o.manager
 * @param {Array<{id?: string}>} o.types The manager's type table — `typeIdx`
 *   indexes it, and each entry's `id` decides its faction.
 * @param {LevelMapData} o.map
 * @param {FactionRules} o.factions The game's faction table — INJECTED, see the
 *   header. Nothing here hard-codes a crew.
 * @param {any} [o.traffic] A `Traffic` director, or null for a city with no
 *   cars (the seat half then does nothing).
 * @param {any} [o.collision] `CollisionGrid`; spawns test `blocked()`.
 * @param {(x: number, z: number) => number} [o.heightAt]
 * @param {import('../types.js').Bounds2|null} [o.bounds]
 * @param {string|number} [o.seed='gameplay']
 * @param {Partial<typeof POP>} [o.tune]
 * @returns {Object} `{name, update, territoryOf, stats, dispose, ...}`
 */
export function createPopulation(o) {
  const {
    manager,
    types = [],
    map,
    factions,
    traffic = null,
    collision = null,
    heightAt = () => 0,
    bounds = null,
    seed = 'gameplay',
    tune = null,
  } = o ?? {};

  if (!manager) throw new Error('createPopulation: a manager is required');
  if (!map) throw new Error('createPopulation: a LevelMapData is required');
  if (!factions) throw new Error('createPopulation: a FactionRules table is required');

  const P = tune ? { ...POP, ...tune } : POP;
  const CIVILIAN = factions.CIVILIAN;
  const GANGS = factions.GANGS;
  const factionForId = factions.factionForId;

  // ── streams (rule 4: one per concern, so adding a draw cannot reshuffle) ──
  const rng = streamRng(seed, 'population');
  const territoryRng = streamRng(seed, 'population.territory');

  // ── the world's shape ─────────────────────────────────────────────────────
  const districts = map.districts ?? [];
  const pavement = buildPavementGraph(districts);
  /** Every town on the board. One island publishes one; an archipelago several. */
  const cityRects = cityRectsOf(map);
  /**
   * WHICH CREWS RUN WHICH TOWN, resolved once. The game's table wins; a level
   * may publish `crews` on its own plateau rect (nothing does yet, and that is
   * the seam for a level whose geography is generated rather than authored);
   * neither means the even wedges this file always drew.
   * @type {Array<any>}
   */
  const territorySpec = cityRects.map((r, i) => ({
    ...r,
    crews: factions.crewsFor?.({ id: r.id, kind: 'city' }, i) ?? /** @type {any} */ (r).crews ?? null,
  }));
  const territories = buildTerritories(districts, {
    rng: territoryRng,
    cities: territorySpec,
    gangs: GANGS,
    neutral: CIVILIAN,
  });

  /**
   * The countryside's settlements, with the crew that runs each (or the
   * civilian id) and its own loitering points. Empty for a level that publishes
   * no `villages` — see {@link villagesOf} — and every path below then reads as
   * "there is nothing out there", which is exactly the countryside this file
   * now describes.
   */
  const villages = villagesOf(map).map((v, i) => {
    const rows = factions.crewsFor?.({ id: v.name, kind: 'village' }, i) ?? null;
    // A hamlet is ONE place, not a town with sides: it takes the first row it
    // understands and never carves anything.
    const row = Array.isArray(rows) ? rows.find((r) => GANGS.includes(r?.gang)) : null;
    return {
      ...v,
      crew: row ? row.gang : CIVILIAN,
      live: Number.isFinite(row?.live) && row.live > 0 ? Math.floor(row.live) : 0,
      points: villagePoints(v, i, map, heightAt),
    };
  });

  /**
   * Is this point in TOWN — within {@link CITY_MARGIN} of ANY city's plateau?
   *
   * ⚠ ANY, not the first. It decides which population the focus is worth
   * (`cityPeds` against `countryPeds`) and whether crews turn out at all, so
   * testing only `cityRects[0]` made every pedestrian on the second island a
   * countryside stroller — six of them, ambling between beach loitering points,
   * on a pavement, in the middle of a downtown.
   *
   * @param {number} x @param {number} z
   */
  const inCity = (x, z) => {
    for (let i = 0; i < cityRects.length; i++) {
      const r = cityRects[i];
      if (x > r.x - CITY_MARGIN && x < r.x + r.w + CITY_MARGIN
        && z > r.z - CITY_MARGIN && z < r.z + r.d + CITY_MARGIN) return true;
    }
    return false;
  };

  /**
   * Loitering points OUTSIDE the city: country-road shoulders and a band just
   * inland of the waterline. Built once, filtered against the sea, and used
   * both as spawn candidates and as walk targets — the countryside has no
   * pavement ring to walk, so its civilians simply amble between these.
   */
  const country = [...buildCountryPoints(map, heightAt), ...villages.flatMap((v) => v.points)];

  /**
   * Which VILLAGE this point belongs to, or -1. Nearest centre wins; `reach` is
   * added to the village's own radius, so the spawn budget can ask "is there a
   * hamlet close enough to be worth populating" with the same call `territoryOf`
   * uses to ask "am I standing in one" (reach 0).
   *
   * @param {number} x @param {number} z @param {number} [reach=0]
   * @returns {number} Index into {@link villages}.
   */
  const villageAt = (x, z, reach = 0) => {
    let best = -1;
    let bestD2 = Infinity;
    for (let i = 0; i < villages.length; i++) {
      const v = villages[i];
      const lim = v.r + reach;
      const d2 = (v.x - x) ** 2 + (v.z - z) ** 2;
      if (d2 > lim * lim || d2 >= bestD2) continue;
      bestD2 = d2;
      best = i;
    }
    return best;
  };

  // ── type routing ──────────────────────────────────────────────────────────
  /** @type {number[]} Type indices whose def id is a civilian. */
  const civTypes = [];
  /** @type {Map<number, number[]>} Type indices per gang faction. */
  const gangTypes = new Map(GANGS.map((f) => [f, /** @type {number[]} */ ([])]));
  /** @type {Int32Array} Faction per TYPE index — the manager's `team` on spawn. */
  const factionOfType = new Int32Array(types.length);
  for (let i = 0; i < types.length; i++) {
    const f = factionForId(types[i]?.id);
    factionOfType[i] = f;
    if (f === CIVILIAN) civTypes.push(i);
    else if (gangTypes.has(f)) /** @type {number[]} */ (gangTypes.get(f)).push(i);
  }

  // ── per-slot bookkeeping (the manager owns the actor, we own its errand) ──
  const cap = manager.cap;
  /** Pavement node a strolling civilian is walking to; -1 = not on the graph. */
  const nodeOf = new Int32Array(cap).fill(-1);
  /** Which way round its ring it is going. */
  const dirOf = new Int8Array(cap).fill(1);
  /** 1 = this slot is ours to cull; the seated ones belong to their car. */
  const owned = new Uint8Array(cap);

  // ── occupants ─────────────────────────────────────────────────────────────
  const maxCars = traffic?.maxDriving ?? 0;
  /** Seats per car: 0 driver, 1 front passenger, 2/3 the rear pair. */
  const SEATS = 4;
  /** @type {Int32Array[]} Manager slot per (seat, car), or -1. */
  const carSeats = Array.from({ length: SEATS }, () => new Int32Array(Math.max(1, maxCars)).fill(-1));
  const carDriver = carSeats[0];
  const carPassenger = carSeats[1];
  /** 1 = this car's driver is dead; it is stopped for good. */
  const carDriverless = new Uint8Array(Math.max(1, maxCars));
  /** Manager slot -> car slot, so a dead occupant can find its vehicle. */
  const carOfSlot = new Int32Array(cap).fill(-1);
  /** @type {Rng[]} One stream per car SLOT — see the driver-determinism note. */
  const carRng = Array.from({ length: Math.max(1, maxCars) }, (_, i) =>
    mulberry32(hashSeed(`${seed}:occupants:${i}`)));

  // ── ring caches + scratch ─────────────────────────────────────────────────
  const candidates = new Int32Array(Math.max(1, pavement.n));
  let candN = 0;
  let candX = NaN;
  let candZ = NaN;
  let spawnAcc = 0;
  let disposed = false;
  const seatOut = { x: 0, y: 0, z: 0, yaw: 0 };
  const cos = Math.cos(P.coneDeg * DEG);

  let cityPeds = 0;
  let countryPeds = 0;
  let gangs = 0;

  // ── territory lookup ──────────────────────────────────────────────────────

  /**
   * Which crew owns this patch of ground. A VILLAGE owns its own disc; inside a
   * block it is that block's owner; anywhere else (a road, a park, the open
   * countryside) it is the nearest block's, which is the reading a player would
   * give it anyway.
   *
   * ⚠ It returns the CIVILIAN id for ground NOBODY runs, and that is no longer
   * only "a level with no districts": most of Fairview belongs to no crew. Every
   * caller already had to handle it (`tryGang` finds no pool, `seatOccupant`
   * falls back to a civilian driver, `placement.js` walks its ladder), which is
   * why a town of pockets needed no new branch anywhere.
   *
   * Exported on the system because the minimap and the wanted-level response
   * both want it.
   *
   * @param {number} x @param {number} z
   * @returns {number} A gang faction id, or the CIVILIAN one.
   */
  const territoryOf = (x, z) => {
    const v = villageAt(x, z);
    if (v >= 0 && villages[v].crew !== CIVILIAN) return villages[v].crew;
    if (!districts.length) return CIVILIAN;
    let best = -1;
    let bestD2 = Infinity;
    for (let i = 0; i < districts.length; i++) {
      const r = districts[i].rect;
      if (x >= r.x && x <= r.x + r.w && z >= r.z && z <= r.z + r.d) return territories.faction[i];
      const dx = Math.max(r.x - x, 0, x - (r.x + r.w));
      const dz = Math.max(r.z - z, 0, z - (r.z + r.d));
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = i;
      }
    }
    return best >= 0 ? territories.faction[best] : CIVILIAN;
  };

  // ── alarms ────────────────────────────────────────────────────────────────

  /**
   * Frighten every civilian within `radius` of a point. THE one entry point for
   * "something scary happened here" — gunfire, a death, a car — so the callers
   * (`Gameplay`, mostly) never touch a manager array.
   *
   * @param {number} x @param {number} z @param {number} radius
   * @returns {number} How many took fright.
   */
  const alarm = (x, z, radius) => {
    const r2 = radius * radius;
    let n = 0;
    for (let i = 0; i < manager.top; i++) {
      if (manager.brain[i] !== BRAIN.CIVILIAN) continue;
      if (!manager.isLive(i)) continue;
      const dx = manager.x[i] - x;
      const dz = manager.z[i] - z;
      if (dx * dx + dz * dz > r2) continue;
      if (manager.scare(i, x, z)) n++;
    }
    return n;
  };

  // ── occupants ─────────────────────────────────────────────────────────────

  /**
   * Seats one occupant in a car.
   *
   * ⚠ BOTH draws happen unconditionally, whatever the pool turns out to be —
   * the same house rule as `onCarActivate`'s passenger roll. A branch that
   * skips a draw makes "who is in car 7" depend on the geometry the car
   * happened to be standing on, which is not reproducible from a seed.
   *
   * @param {number} car Driving slot.
   * @param {number} seat 0 = driver, 1 = front passenger, 2/3 = rear pair.
   * @param {Rng} r The car slot's own stream.
   * @returns {number} Manager slot, or -1.
   */
  const seatOccupant = (car, seat, r) => {
    const roll = r();
    const pick = r();
    const where = traffic.getSeatWorld(car, seat, seatOut);
    if (!where) return -1;
    // Mostly a civilian; sometimes the DRIVER belongs to the crew whose turf
    // the car is on (see `POP.gangDriverChance`).
    let pool = civTypes;
    if (seat === 0 && roll < P.gangDriverChance) {
      const crew = gangTypes.get(territoryOf(where.x, where.z));
      if (crew?.length) pool = crew;
    }
    if (!pool.length) return -1;
    const ti = pool[Math.min(pool.length - 1, (pick * pool.length) | 0)];
    const slot = manager.spawn(ti, where.x, where.z, where.y, factionOfType[ti] ?? CIVILIAN);
    if (slot < 0) return -1;
    manager.setBrain(slot, BRAIN.SEATED);
    manager.placeSeated(slot, where.x, where.y, where.z, where.yaw);
    carOfSlot[slot] = car;
    owned[slot] = 0;
    return slot;
  };

  /**
   * Seats an AUTHORED occupant: a specific type, a specific faction, in a
   * specific seat. The wanted-level response's crews arrive this way.
   *
   * It is the same registration {@link seatOccupant} performs — the slot lands
   * in `carDriver`/`carPassenger`, so the seat loop slaves it, the car cull
   * takes it, {@link releaseOccupant} can hand it over and a CARJACK of a
   * crewed police car drags a real policeman out of it. That reuse is the whole
   * point of the seam: the response never learns what a seat is.
   *
   * ⚠ NO RNG, deliberately, exactly like {@link adopt}: a crew is put in a car
   * at a moment the PLAYER caused, and a draw there would end "same seed, same
   * city" for everything downstream of it.
   *
   * @param {number} car Driving slot — must already be reserved (see
   *   `Traffic#reserved`) or the ambient rule has already filled the seat.
   * @param {number} seat 0 = driver, 1 = front passenger, 2/3 = rear pair.
   * @param {number} typeIdx Index into the manager's type table.
   * @param {number} faction Written to the manager's `team` byte.
   * @returns {number} Manager slot, or -1.
   */
  const seatCrew = (car, seat, typeIdx, faction) => {
    if (disposed || !traffic || car < 0 || car >= maxCars) return -1;
    const arr = carSeats[seat];
    if (!arr || arr[car] >= 0) return -1;
    const where = traffic.getSeatWorld(car, seat, seatOut);
    if (!where) return -1;
    const slot = manager.spawn(typeIdx, where.x, where.z, where.y, faction);
    if (slot < 0) return -1;
    manager.setBrain(slot, BRAIN.SEATED);
    manager.placeSeated(slot, where.x, where.y, where.z, where.yaw);
    arr[car] = slot;
    carOfSlot[slot] = car;
    owned[slot] = 0;
    return slot;
  };

  /**
   * A traffic slot became a live car: put people in it.
   *
   * ⚠ The type draw comes from the CAR SLOT's own stream, not the shared one.
   * That is what makes "who is driving car 7" reproducible for a seed no matter
   * how many pedestrians happened to spawn first.
   * @param {number} car
   */
  const onCarActivate = (car) => {
    if (disposed || car < 0 || car >= maxCars) return;
    // A RESERVED car belongs to whoever spawned it — the wanted-level response,
    // today. It gets its own crew through {@link seatCrew}, and the ambient rule
    // must not put two civilians in a police cruiser on the way past. No draws
    // at all here: the reservation is a player-caused event, so spending the
    // slot's stream on it would make an ordinary saloon's driver depend on
    // whether you were wanted ten minutes ago.
    if (traffic?.isReserved?.(car)) {
      for (const arr of carSeats) arr[car] = -1;
      carDriverless[car] = 0;
      return;
    }
    const r = carRng[car];
    carDriverless[car] = 0;
    // Draw ALL the want-rolls unconditionally, whatever the outcome — a
    // rejected passenger must not shift the stream for the next activation.
    // (Rear seats also depend on the body having a second row, but that is a
    // property of the archetype, which is itself deterministic per car.)
    const wantPassenger = r() < P.passengerChance;
    const wantRearL = r() < P.rearChance;
    const wantRearR = r() < P.rearChance;
    const seats = traffic.seatCount?.(traffic.arch[car]) ?? 2;
    carDriver[car] = seatOccupant(car, 0, r);
    carPassenger[car] = wantPassenger ? seatOccupant(car, 1, r) : -1;
    carSeats[2][car] = wantRearL && seats >= 4 ? seatOccupant(car, 2, r) : -1;
    carSeats[3][car] = wantRearR && seats >= 4 ? seatOccupant(car, 3, r) : -1;
  };

  /**
   * A traffic slot is being retired: take its people with it. A silent
   * `despawn`, never a `kill` — a death animation for someone in a car 120 m
   * behind you is a lie about what happened.
   * @param {number} car
   */
  const onCarDeactivate = (car) => {
    if (car < 0 || car >= maxCars) return;
    for (const arr of carSeats) {
      const slot = arr[car];
      if (slot >= 0) {
        if (manager.isLive(slot)) manager.despawn(slot);
        carOfSlot[slot] = -1;
      }
      arr[car] = -1;
    }
    carDriverless[car] = 0;
  };

  if (traffic) {
    traffic.onActivate = onCarActivate;
    traffic.onDeactivate = onCarDeactivate;
    // Cars that were already driving when the director installed (a scripted
    // scene, a hot reload) get their crews now rather than at their next trip.
    for (let i = 0; i < traffic.top; i++) if (traffic.isDriving(i)) onCarActivate(i);
  }

  /**
   * Slaves every occupant to its seat. Runs AFTER the manager step (which skips
   * seated slots entirely), so the transform a renderer reads is this frame's.
   */
  const slaveSeats = () => {
    if (!traffic) return;
    for (let car = 0; car < maxCars; car++) {
      let any = false;
      for (let s = 0; s < SEATS; s++) if (carSeats[s][car] >= 0) { any = true; break; }
      if (!any || !traffic.isDriving(car)) continue;
      for (let s = 0; s < SEATS; s++) {
        const slot = carSeats[s][car];
        if (slot < 0 || !manager.isLive(slot)) continue;
        const w = traffic.getSeatWorld(car, s, seatOut);
        // Seated, therefore IN the cabin, therefore not drawn at all — the
        // sixth argument defaults to true. A carjack's drag passes false and
        // the man reappears as himself on his way out of the door.
        if (w) manager.placeSeated(slot, w.x, w.y, w.z, w.yaw);
      }
    }
  };

  // ── handing an occupant to somebody else (the carjack) ────────────────────

  /**
   * Gives an occupant up. The director stops slaving it to its seat and stops
   * culling it with the car; the CALLER owns its transform from here until it
   * either {@link reseat}s it or {@link adopt}s it back onto the street.
   *
   * This is a seam rather than a flag because the alternative — a "being
   * dragged" byte the seat loop skips — puts the sequence's business inside the
   * director's step, and the whole point of the population/traffic split is
   * that neither one knows what the other is animating.
   *
   * @param {number} car Driving slot.
   * @param {number} seat 0 = driver, 1 = front passenger, 2/3 = rear pair.
   * @returns {number} Manager slot, or -1 when that seat is empty.
   */
  const releaseOccupant = (car, seat) => {
    if (car < 0 || car >= maxCars) return -1;
    const arr = carSeats[seat];
    if (!arr) return -1;
    const slot = arr[car];
    if (slot < 0) return -1;
    arr[car] = -1;
    carOfSlot[slot] = -1;
    return slot;
  };

  /**
   * Puts a released occupant back in its seat — an aborted sequence rewinding
   * (the player died before the drag started). A no-op unless the slot is
   * still a live SEATED actor, so a victim who was shot in the meantime is
   * simply not reseated.
   *
   * @param {number} car @param {number} seat @param {number} slot
   * @returns {boolean}
   */
  const reseat = (car, seat, slot) => {
    if (car < 0 || car >= maxCars || slot < 0 || !carSeats[seat]) return false;
    if (!manager.isLive(slot) || manager.brain[slot] !== BRAIN.SEATED) return false;
    carSeats[seat][car] = slot;
    carOfSlot[slot] = car;
    owned[slot] = 0;
    return true;
  };

  /**
   * Takes a loose actor onto the director's books: from here it is culled by
   * distance and handed pavement errands like any other ambient spawn. What a
   * carjack victim becomes once he has stopped rolling.
   *
   * ⚠ No rng. The stroll direction comes off the slot's parity instead, because
   * a draw here would be a draw the population stream takes at a moment
   * decided by the PLAYER — and "same seed, same city" would stop being true.
   *
   * @param {number} slot
   */
  const adopt = (slot) => {
    if (slot < 0 || slot >= cap || !manager.isLive(slot)) return;
    owned[slot] = 1;
    nodeOf[slot] = -1;
    dirOf[slot] = slot & 1 ? 1 : -1;
  };

  // ── death / teardown notifications ────────────────────────────────────────

  /**
   * An actor died. Two consequences, and both are the population's business
   * rather than combat's: a death frightens the neighbourhood, and a dead
   * DRIVER parks the car.
   *
   * @param {number} slot
   */
  const notifyDeath = (slot) => {
    if (disposed) return;
    alarm(manager.x[slot], manager.z[slot], PED_TUNING.deathRadius);
    const car = carOfSlot[slot];
    if (car < 0 || car >= maxCars) return;
    if (carDriver[car] === slot) setDriverless(car);
  };

  /**
   * This car has nobody at the wheel. It brakes through the director's own
   * seam and stays where it stopped — which is also exactly what it becomes
   * when a carjack is ABORTED after the driver is already out, so the two
   * paths share the one statement of what a driverless car is.
   *
   * `systems/Carjack.js` reads {@link isDriverless} and treats such a car as a
   * PLAIN entry: there is nobody left to drag, and the leftover hold is
   * cleared by `Traffic#takeDriving` on the way through.
   * @param {number} car Driving slot.
   */
  const setDriverless = (car) => {
    if (car < 0 || car >= maxCars) return;
    carDriverless[car] = 1;
    traffic?.requestStop?.(car);
  };

  /**
   * A manager slot went free (any reason). Drops our bookkeeping so a recycled
   * slot cannot inherit a car seat or a pavement errand.
   * @param {number} slot
   */
  const onSlotFreed = (slot) => {
    const car = carOfSlot[slot];
    if (car >= 0 && car < maxCars) {
      for (const arr of carSeats) if (arr[car] === slot) arr[car] = -1;
      carOfSlot[slot] = -1;
    }
    nodeOf[slot] = -1;
    owned[slot] = 0;
  };

  // ── waypoints ─────────────────────────────────────────────────────────────

  /**
   * Nearest pavement node to a point, or -1 beyond `maxD`.
   * @param {number} x @param {number} z @param {number} maxD
   */
  const nearestNode = (x, z, maxD) => {
    let best = -1;
    let bestD2 = maxD * maxD;
    for (let i = 0; i < pavement.n; i++) {
      const dx = pavement.x[i] - x;
      const dz = pavement.z[i] - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = i;
      }
    }
    return best;
  };

  /**
   * Hands slot `i` its next errand.
   *
   * On the graph it is one array read: step to the next node round the ring,
   * or — at a corner, sometimes — take the crossing to the block opposite. Off
   * the graph (the countryside, or a civilian that just finished running) it
   * re-anchors: the nearest pavement node if there is one within reach,
   * otherwise a country loitering point, otherwise a short random amble.
   * @param {number} i
   */
  const assignGoal = (i) => {
    let node = nodeOf[i];
    if (node >= 0) {
      const dx = pavement.x[node] - manager.x[i];
      const dz = pavement.z[node] - manager.z[i];
      if (dx * dx + dz * dz > P.reanchor * P.reanchor) node = -1;
    }
    if (node < 0) node = nearestNode(manager.x[i], manager.z[i], P.reanchor);

    if (node >= 0) {
      const hop = pavement.corner[node] && pavement.hop[node] >= 0 && rng() < P.hopChance
        ? pavement.hop[node]
        : -1;
      const step = hop >= 0 ? hop : (dirOf[i] > 0 ? pavement.next[node] : pavement.prev[node]);
      const to = step >= 0 ? step : node;
      nodeOf[i] = to;
      manager.setGoal(i, pavement.x[to], pavement.z[to]);
      return;
    }

    nodeOf[i] = -1;
    const pt = pickCountryNear(manager.x[i], manager.z[i], P.countryGoal, rng);
    if (pt) {
      manager.setGoal(i, pt.x, pt.z);
      return;
    }
    const a = rng() * TAU;
    const d = range(rng, 8, 20);
    manager.setGoal(i, manager.x[i] + Math.sin(a) * d, manager.z[i] + Math.cos(a) * d);
  };

  /**
   * A country loitering point within `maxD`, chosen by reservoir sampling so
   * exactly one rng number is drawn per candidate and the pick is uniform.
   * @param {number} x @param {number} z @param {number} maxD @param {Rng} r
   * @returns {{x: number, z: number}|null}
   */
  const pickCountryNear = (x, z, maxD, r) => {
    if (!country.length) return null;
    const max2 = maxD * maxD;
    let chosen = null;
    let seen = 0;
    for (const p of country) {
      const dx = p.x - x;
      const dz = p.z - z;
      if (dx * dx + dz * dz > max2) continue;
      seen++;
      if (r() * seen < 1) chosen = p;
    }
    return chosen;
  };

  // ── spawning ──────────────────────────────────────────────────────────────

  /**
   * Pavement nodes inside the spawn annulus, cached until the focus has moved
   * far enough to matter — the same trick, and the same reason, as
   * `Traffic#_refreshCandidates`.
   * @param {number} fx @param {number} fz
   */
  const refreshCandidates = (fx, fz) => {
    if (Math.hypot(fx - candX, fz - candZ) < P.candidateRefresh) return;
    candX = fx;
    candZ = fz;
    const lo = (P.ringMin - 10) ** 2;
    const hi = (P.ringMax + 10) ** 2;
    let n = 0;
    for (let i = 0; i < pavement.n; i++) {
      const dx = pavement.x[i] - fx;
      const dz = pavement.z[i] - fz;
      const d2 = dx * dx + dz * dz;
      if (d2 >= lo && d2 <= hi) candidates[n++] = i;
    }
    candN = n;
  };

  /**
   * Is a world point a legal place to put somebody? Ring distance, the camera
   * cone, the level bounds and the collision grid, in that order — cheapest
   * rejection first.
   * @param {number} x @param {number} z @param {number} r Actor radius.
   * @param {number} fx @param {number} fz @param {number} dirX @param {number} dirZ
   * @param {number} [minD] Inner radius. Defaults to `ringMin`; a VILLAGE
   *   passes the smaller {@link POP.villageRingMin}, because a hamlet does not
   *   reach as far as the ordinary ring does.
   */
  const legal = (x, z, r, fx, fz, dirX, dirZ, minD = P.ringMin) => {
    const dx = x - fx;
    const dz = z - fz;
    const d = Math.hypot(dx, dz);
    if (d < minD || d > P.ringMax) return false;
    if ((dx / d) * dirX + (dz / d) * dirZ > cos) return false;
    const b = bounds;
    if (b && (x < b.minX + r || x > b.maxX - r || z < b.minZ + r || z > b.maxZ - r)) return false;
    if (collision?.blocked?.(x, z, r)) return false;
    return true;
  };

  /**
   * How many of `faction` the director keeps alive around the player while they
   * are in city `city`. The crew row's `live` when the spec set one, else
   * {@link POP.gangPerTerritory}.
   *
   * ⚠ It is a per-PLACE number read through the block the candidate node stands
   * on, but the count it is compared against (`aliveOnTeam`) is GLOBAL. That is
   * deliberate and it is only honest because the spawn ring is 70 m wide: the
   * player is in one town at a time, and a crew's men 900 m away are not part of
   * the street you are standing in.
   *
   * @param {number} city @param {number} faction
   * @returns {number}
   */
  const liveCap = (city, faction) => {
    const crew = GANGS.indexOf(faction);
    const want = crew >= 0 ? territories.liveOf(city, crew) : 0;
    return want > 0 ? want : P.gangPerTerritory;
  };

  /**
   * Spawns one ambient CIVILIAN at a fixed point. The shared half of the three
   * pedestrian paths — the only difference between a pavement stroller and a
   * village one is which node it remembers, and that is the argument.
   *
   * @param {{x: number, z: number}} p @param {number} node Pavement node, or -1.
   * @param {number} [minD] Inner spawn radius — see {@link legal}.
   * @returns {boolean}
   */
  const spawnCivilian = (p, node, fx, fz, dirX, dirZ, minD = P.ringMin) => {
    if (!civTypes.length) return false;
    const ti = civTypes[Math.min(civTypes.length - 1, (rng() * civTypes.length) | 0)];
    const r = manager.types[ti]?.radius ?? 0.4;
    if (!legal(p.x, p.z, r, fx, fz, dirX, dirZ, minD)) return false;
    const slot = manager.spawn(ti, p.x, p.z, heightAt(p.x, p.z), CIVILIAN);
    if (slot < 0) return false;
    manager.setBrain(slot, BRAIN.CIVILIAN);
    owned[slot] = 1;
    nodeOf[slot] = node;
    dirOf[slot] = rng() < 0.5 ? 1 : -1;
    assignGoal(slot);
    return true;
  };

  /**
   * One attempt at a pedestrian on a pavement.
   * @returns {boolean}
   */
  const tryCityPed = (fx, fz, dirX, dirZ) => {
    if (!candN || !civTypes.length) return false;
    const node = candidates[Math.min(candN - 1, (rng() * candN) | 0)];
    return spawnCivilian({ x: pavement.x[node], z: pavement.z[node] }, node, fx, fz, dirX, dirZ);
  };

  /** One attempt at a pedestrian out of town. @returns {boolean} */
  const tryCountryPed = (fx, fz, dirX, dirZ) => {
    if (!country.length || !civTypes.length) return false;
    const p = country[Math.min(country.length - 1, (rng() * country.length) | 0)];
    return spawnCivilian(p, -1, fx, fz, dirX, dirZ);
  };

  /**
   * One attempt at a VILLAGER: the same pedestrian, drawn from one hamlet's own
   * loitering points instead of from the whole island's. Restricting the pool is
   * what makes a village readable at `POP.villagePeds` — five people picked out
   * of every road shoulder and beach on a 2000 m board would put nobody in the
   * village you are standing in.
   *
   * @param {number} v Index into `villages`. @returns {boolean}
   */
  const tryVillagePed = (v, fx, fz, dirX, dirZ) => {
    const pts = villages[v]?.points ?? [];
    if (!pts.length || !civTypes.length) return false;
    const p = pts[Math.min(pts.length - 1, (rng() * pts.length) | 0)];
    return spawnCivilian(p, -1, fx, fz, dirX, dirZ, P.villageRingMin);
  };

  /**
   * One attempt at a crew member in a VILLAGE. A hamlet is a whole territory —
   * a small one — so the crew that runs it turns out here exactly as it does on
   * a city block, at its own `live` budget.
   *
   * @param {number} v Index into `villages`. @returns {boolean}
   */
  const tryVillageGang = (v, fx, fz, dirX, dirZ) => {
    const village = villages[v];
    const pts = village?.points ?? [];
    if (!pts.length) return false;
    const faction = village.crew;
    const pool = gangTypes.get(faction);
    if (!pool?.length) return false;
    const cap = village.live > 0 ? village.live : P.gangPerTerritory;
    if (manager.aliveOnTeam(faction) >= cap) return false;
    const p = pts[Math.min(pts.length - 1, (rng() * pts.length) | 0)];
    const roll = rng();
    const ti = pool[Math.min(pool.length - 1, (roll * pool.length) | 0)];
    const r = manager.types[ti]?.radius ?? 0.4;
    if (!legal(p.x, p.z, r, fx, fz, dirX, dirZ, P.villageRingMin)) return false;
    const slot = manager.spawn(ti, p.x, p.z, heightAt(p.x, p.z), faction);
    if (slot < 0) return false;
    owned[slot] = 1;
    return true;
  };

  /**
   * One attempt at a gang member. The candidate node decides WHICH crew — a
   * territory is a place, so who turns up on a corner is a property of the
   * corner and not of a roster roll. That is what puts two crews in one street
   * at a border and starts the firefight.
   * @returns {boolean}
   */
  const tryGang = (fx, fz, dirX, dirZ) => {
    if (!candN) return false;
    const node = candidates[Math.min(candN - 1, (rng() * candN) | 0)];
    const roll = rng();
    const x = pavement.x[node];
    const z = pavement.z[node];
    const block = pavement.block[node];
    const faction = territories.faction[block] ?? CIVILIAN;
    const pool = gangTypes.get(faction);
    // Neutral ground has no pool, so a town whose spec left most of its blocks
    // to nobody simply spawns nobody there. That is the whole implementation of
    // "mostly civilian" — no branch, no flag.
    if (!pool?.length) return false;
    if (manager.aliveOnTeam(faction) >= liveCap(territories.city[block], faction)) return false;
    const ti = pool[Math.min(pool.length - 1, (roll * pool.length) | 0)];
    const r = manager.types[ti]?.radius ?? 0.4;
    if (!legal(x, z, r, fx, fz, dirX, dirZ)) return false;
    const slot = manager.spawn(ti, x, z, heightAt(x, z), faction);
    if (slot < 0) return false;
    owned[slot] = 1;
    return true;
  };

  // ── the step ──────────────────────────────────────────────────────────────

  /**
   * One fixed step.
   *
   * @param {number} dt
   * @param {{x: number, z: number}} focus Usually the player.
   * @param {number} [camYaw=0] Live rig yaw — the spawn cone needs it.
   * @param {{x: number, z: number, speed: number, on: boolean}|null} [hazard]
   *   The player's CAR, when they are driving one. A fast one scares people.
   */
  const update = (dt, focus, camYaw = 0, hazard = null) => {
    if (disposed || !(dt > 0) || !focus) return;
    const fx = focus.x;
    const fz = focus.z;
    // The camera sits behind the player looking along `-(sin yaw, cos yaw)`.
    const dirX = -Math.sin(camYaw);
    const dirZ = -Math.cos(camYaw);

    refreshCandidates(fx, fz);

    // ── 1. cull + census + errands, in ONE pass over the pool ───────────────
    const far2 = P.despawn * P.despawn;
    cityPeds = 0;
    countryPeds = 0;
    gangs = 0;
    for (let i = 0; i < manager.top; i++) {
      if (manager.state[i] === STATE.DEAD) continue;
      const brain = manager.brain[i];
      // A passenger is culled with its car, never by distance — pulling one out
      // of a moving vehicle leaves an empty driving seat on screen.
      if (brain === BRAIN.SEATED) continue;
      if (!owned[i]) continue;
      const dx = manager.x[i] - fx;
      const dz = manager.z[i] - fz;
      if (dx * dx + dz * dz > far2) {
        manager.despawn(i);
        continue;
      }
      if (brain === BRAIN.CIVILIAN) {
        if (nodeOf[i] >= 0) cityPeds++;
        else countryPeds++;
        if (manager.needsGoal(i)) assignGoal(i);
      } else {
        gangs++;
      }
    }

    // ── 2. the player's car as a hazard ────────────────────────────────────
    if (hazard?.on && Math.abs(hazard.speed) > PED_TUNING.carSpeed) {
      alarm(hazard.x, hazard.z, PED_TUNING.carRadius);
    }

    // ── 3. seats ───────────────────────────────────────────────────────────
    slaveSeats();

    // ── 4. top up ──────────────────────────────────────────────────────────
    spawnAcc += dt * P.rate;
    let attempts = Math.floor(spawnAcc);
    spawnAcc -= attempts;
    if (attempts <= 0) return;

    // Is the focus in town — in ANY town? That decides which pavement a
    // pedestrian wants, and it is a question about the whole archipelago.
    // Out of town there is one more question: is there a VILLAGE close enough
    // to be worth filling? `ringMax` is the reach, so a hamlet has people in it
    // by the time you are near enough to see them and none once you have driven
    // past — the open road between two towns carries `countryPeds`, which is 0.
    const inTown = inCity(fx, fz);
    const village = inTown ? -1 : villageAt(fx, fz, P.ringMax);
    const wantCity = inTown ? P.cityPeds : 0;
    const wantCountry = inTown ? 0 : (village >= 0 ? P.villagePeds : P.countryPeds);

    while (attempts-- > 0) {
      // Gangs first: they are the scarcer population and the ring fills fast.
      // ⚠ ONE cap here, `gangMax`. The per-crew budget is per PLACE now
      // (`liveCap`), so a second global expression written in terms of a flat
      // per-territory number would be a cap nobody could reason about — a town
      // with a sole owner and a hamlet with two men would both be measured
      // against a product of the roster length.
      if (gangs < P.gangMax) {
        if (inTown) {
          for (let k = 0; k < P.tries; k++) if (tryGang(fx, fz, dirX, dirZ)) { gangs++; break; }
        } else if (village >= 0) {
          for (let k = 0; k < P.tries; k++) if (tryVillageGang(village, fx, fz, dirX, dirZ)) { gangs++; break; }
        }
      }
      if (cityPeds < wantCity) {
        for (let k = 0; k < P.tries; k++) if (tryCityPed(fx, fz, dirX, dirZ)) { cityPeds++; break; }
      } else if (countryPeds < wantCountry) {
        for (let k = 0; k < P.tries; k++) {
          const got = village >= 0
            ? tryVillagePed(village, fx, fz, dirX, dirZ)
            : tryCountryPed(fx, fz, dirX, dirZ);
          if (got) { countryPeds++; break; }
        }
      }
    }
  };

  return {
    name: 'population',
    pavement,
    territories,
    country,
    /** Every town's plateau rect, normalised — see {@link cityRectsOf}. */
    cityRects,
    /**
     * Every village, with the crew that runs it and its loitering points.
     * Empty on a level that publishes none — see {@link villagesOf}.
     */
    villages,
    /** Is this point in ANY town? The spawn budget's one geometry question. */
    inCity,
    /** Which village is this point in (or near, with a `reach`)? -1 for none. */
    villageAt,
    territoryOf,
    alarm,
    notifyDeath,
    onSlotFreed,
    update,
    // ── the carjack's seams (see `systems/Carjack.js`) ──
    releaseOccupant,
    reseat,
    adopt,
    setDriverless,
    /** The wanted-level response's seam — see {@link seatCrew}. */
    seatCrew,
    /** Is this car's driver gone (shot, or dragged out)? */
    isDriverless: (car) => car >= 0 && car < maxCars && carDriverless[car] === 1,
    /** Exposed for tests + the carjack sequence. */
    driverOf: (car) => (car >= 0 && car < maxCars ? carDriver[car] : -1),
    passengerOf: (car) => (car >= 0 && car < maxCars ? carPassenger[car] : -1),
    /** Any seat, 0..3 — the generalised form of the two above. */
    occupantOf: (car, seat) => (car >= 0 && car < maxCars && carSeats[seat] ? carSeats[seat][car] : -1),

    /** @returns {Object} Live counters for the debug read-out. */
    stats() {
      let seated = 0;
      let fleeing = 0;
      for (let i = 0; i < manager.top; i++) {
        if (!manager.isLive(i)) continue;
        if (manager.brain[i] === BRAIN.SEATED) seated++;
        else if (manager.brain[i] === BRAIN.CIVILIAN && manager.mood[i] === PED.FLEE) fleeing++;
      }
      return {
        cityPeds,
        countryPeds,
        gangs,
        seated,
        fleeing,
        nodes: pavement.n,
        rings: pavement.rings,
        crossings: pavement.hops,
        countryPoints: country.length,
        villages: villages.length,
        territories: territories.counts,
        /** Blocks no crew runs — most of a town whose spec is all pockets. */
        unclaimed: territories.unclaimed,
      };
    },

    /** Idempotent. Frees every actor this director put in the world. */
    dispose() {
      if (disposed) return;
      disposed = true;
      if (traffic) {
        traffic.onActivate = null;
        traffic.onDeactivate = null;
      }
      for (let i = 0; i < manager.top; i++) {
        if (!manager.isLive(i)) continue;
        if (owned[i] || manager.brain[i] === BRAIN.SEATED) manager.despawn(i);
      }
      for (const arr of carSeats) arr.fill(-1);
      carOfSlot.fill(-1);
    },
  };
}

/**
 * THE CRIME EMITTER. One payload shape, one file, one rule: only things the
 * PLAYER did come through here.
 *
 * `attachFactionCombat` uses it for the `shot_`/`killed_` verbs;
 * `systems/Carjack.js` uses it for `'carjack'`, which is not damage and so has
 * no manager hook to hang off. Keeping it a plain function rather than a
 * second `bus.emit` at the call site is what stops the taxonomy from growing a
 * dialect per caller — the wanted-level system reads ONE shape.
 *
 * The position is the VICTIM'S, read at emit time: for a carjack that is where
 * he landed on the tarmac, which is where the crime visibly happened.
 *
 * @param {{emit: Function}|null} bus
 * @param {EnemyManager} manager
 * @param {string} type `shot_civilian` | `killed_gang` | `carjack` | ...
 * @param {number} victim Manager slot.
 */
export function emitCrime(bus, manager, type, victim) {
  if (victim < 0) return;
  bus?.emit?.('crime', {
    type,
    x: manager.x[victim],
    z: manager.z[victim],
    faction: manager.team[victim],
    victim,
  });
}

/**
 * Wires the FACTION consequences of combat onto a manager: who gets provoked,
 * what counts as a crime, and who hears the shot.
 *
 * It lives here rather than in `Gameplay.js` for one reason: `Gameplay` imports
 * three.js and this does not, so putting the taxonomy here is what lets
 * `tests/factions.test.mjs` prove `shot_civilian` and `killed_gang` in node.
 * `Gameplay` keeps the parts that genuinely need a GPU — the tracer and the
 * blood on `onFire`.
 *
 * THE TAXONOMY: `'crime'` fires with `type = "<verb>_<side>"`, verb in
 * `shot` / `killed`, side in `civilian` / `gang` / `police`, plus `{x, z}`
 * where it happened, the victim's `faction` id and its manager `victim` slot.
 * It fires ONLY for damage the PLAYER dealt — a gang shooting a rival is not a
 * crime, and the wanted-level system must never bill you for one. The
 * non-damage verb `'carjack'` shares the payload and the rule; it is emitted
 * by `systems/Carjack.js` through {@link emitCrime}, the same door.
 *
 * @param {Object} o
 * @param {EnemyManager} o.manager
 * @param {{emit: Function}} o.bus
 * @param {FactionRules} o.factions
 * @param {any} [o.population] Optional director — deaths are reported to it.
 * @returns {() => void} Detach. Idempotent.
 */
export function attachFactionCombat({ manager, bus, factions, population = null }) {
  /**
   * Which SIDE a faction reads as. Derived from the rules table rather than
   * from a hard-coded police id, so a game that adds a fourth crew needs no
   * edit here.
   * @param {number} f
   */
  const sideOf = (f) => (f === factions.CIVILIAN ? 'civilian' : factions.GANGS.includes(f) ? 'gang' : 'police');

  /** @param {string} verb @param {number} victim */
  const crime = (verb, victim) => emitCrime(bus, manager, `${verb}_${sideOf(manager.team[victim])}`, victim);

  manager.onDamage = (victim, src) => {
    // Only the player's rounds have consequences the world has to remember.
    // Gangs are permanently hostile to each other, so a stray round between
    // them needs no state at all — the static matrix already covers it.
    if (src !== factions.PLAYER) return;
    factions.state.provoke(manager.team[victim]);
    crime('shot', victim);
  };

  manager.onKill = (victim, m) => {
    if (m.lastAttacker === factions.PLAYER) crime('killed', victim);
    // A death frightens the neighbourhood and, if it was a driver, parks the
    // car. Both belong to the director, not to combat.
    population?.notifyDeath(victim);
  };

  return () => {
    manager.onDamage = null;
    manager.onKill = null;
  };
}

/**
 * Loitering points outside the city: country-road shoulders every few samples,
 * plus a band just inland of the waterline (the beach). Everything below sea
 * level plus a hand's breadth is dropped, so nobody strolls into the surf.
 *
 * @param {LevelMapData} map
 * @param {(x: number, z: number) => number} heightAt
 * @returns {Array<{x: number, z: number}>}
 */
/**
 * One village's loitering points: two rings of them inside the settlement's
 * disc, which is where its people stand, walk between and are spawned on.
 *
 * ⚠ NO RNG, exactly like the pavement graph, and for the same reason — this is
 * derived geometry, not a population. The per-village phase comes off the
 * INDEX, so two hamlets do not have identically-arranged residents and neither
 * one moves when the other is added or removed.
 *
 * ⚠ The points are only CANDIDATES. Anything a house or a fence stands on is
 * rejected at spawn time by the collision grid (`legal`), so this deliberately
 * knows nothing about what the village generator put where — the two passes
 * never have to agree about a building's footprint.
 *
 * @param {{x: number, z: number, r: number}} v
 * @param {number} i Index — the ring phase, so hamlets differ deterministically.
 * @param {LevelMapData} map
 * @param {(x: number, z: number) => number} heightAt
 * @returns {Array<{x: number, z: number}>}
 */
function villagePoints(v, i, map, heightAt) {
  /** @type {Array<{x: number, z: number}>} */
  const out = [];
  const sea = (map?.seaLevel ?? 0) + 0.3;
  const phase = i * 0.7;
  for (const [count, f] of [[6, 0.45], [10, 0.82]]) {
    for (let k = 0; k < count; k++) {
      const a = phase + (k / count) * TAU;
      const x = v.x + Math.cos(a) * v.r * f;
      const z = v.z + Math.sin(a) * v.r * f;
      if (heightAt(x, z) <= sea) continue;
      out.push({ x, z });
    }
  }
  return out;
}

function buildCountryPoints(map, heightAt) {
  /** @type {Array<{x: number, z: number}>} */
  const out = [];
  const sea = (map.seaLevel ?? 0) + 0.3;
  const keep = (x, z) => {
    if (heightAt(x, z) <= sea) return;
    out.push({ x, z });
  };

  for (const r of map.roads?.country ?? []) {
    const pts = r.points ?? [];
    if (r.kind === 'head' || pts.length < 4) continue;
    const off = (r.width ?? 6) / 2 + 1.6;
    for (let k = 1; k + 1 < pts.length; k += 3) {
      const a = pts[k];
      const b = pts[k + 1];
      const l = Math.hypot(b.x - a.x, b.z - a.z);
      if (l < 1e-3) continue;
      const dx = (b.x - a.x) / l;
      const dz = (b.z - a.z) / l;
      keep(a.x - dz * off, a.z + dx * off);
      keep(a.x + dz * off, a.z - dx * off);
    }
  }

  // The beach: each coastline pulled inland. A ring is CLOSED and ORDERED and
  // its first point is NOT repeated (types.js), so the normal comes from the
  // WRAPPED neighbour.
  //
  // ⚠ ONE LOOP PER ISLAND, AND THE WRAP IS INSIDE IT. `coastRings` hands back a
  // list precisely so this cannot be written as one walk over a concatenation:
  // that walk would pair the last point of island A with the first point of
  // island B, take the perpendicular of a segment spanning the whole strait for
  // a shoreline normal, and drop a loitering point 7 m off it — out in the
  // water, or 7 m along a beach it has nothing to do with. Nothing downstream
  // would notice: the point is a plain `{x, z}` and a civilian would walk to it.
  // The per-ring stride also restarts, so island B is sampled at its own phase
  // and a second island cannot move one metre of the first island's countryside.
  for (const coast of coastRings(map)) {
    const n = coast.length;
    for (let i = 0; i < n; i += 6) {
      const a = coast[i];
      const b = coast[(i + 1) % n];
      const l = Math.hypot(b.x - a.x, b.z - a.z);
      if (l < 1e-3) continue;
      // Inward is whichever side of the tangent is above water — cheaper and
      // more robust than trusting a winding order.
      const nx = -(b.z - a.z) / l;
      const nz = (b.x - a.x) / l;
      const d = 7;
      if (heightAt(a.x + nx * d, a.z + nz * d) > sea) keep(a.x + nx * d, a.z + nz * d);
      else keep(a.x - nx * d, a.z - nz * d);
    }
  }

  return out;
}
